import localStorageService from "utils/localStorageService";
import { workspaceToDefaultPath } from "common/Home/General/constants.tsx";
import { workspaceToPaths } from "common/Home/Sider/Sider";
import {
  generatePath,
  matchPath,
  useHistory,
  useLocation,
} from "react-router-dom";
import { WorkspaceType } from "@shared/types";
import {
  QUERY_BOT_TYPE_TO_BOT_TYPE,
  WORKSPACE_STORAGE_KEY,
} from "@shared/common/constants";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { BASE_BOTS_PATH, BASE_BOTS_WITH_ACTION_PATH } from "common/constants";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import useQuery from "utils/hooks/useQuery";
import { APPLIED_BACKTESTING_QUERY_PARAM } from "features/bots/common/constants";
import { getBotFormRoute } from "utils/routes";

const useWorkspace = () => {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const localStorageWorkspace = localStorageService.getValue(
    WORKSPACE_STORAGE_KEY
  );
  const {
    workspace: currentWorkspace,
    setWorkspaceType,
    realUser,
    isWorkspaceValid,
  } = useSharedWorkspace({ allowAdminWorkspace: true });

  const isPathInWorkspace = (workspace: WorkspaceType, pathname: string) => {
    if (location.pathname === "/") return true;
    return workspaceToPaths[workspace]?.some((path) =>
      matchPath(pathname, {
        path: path,
        exact: false,
        strict: false,
      })
    );
  };

  const updateBotActionPath = () => {
    const match = matchPath(location.pathname, {
      path: BASE_BOTS_WITH_ACTION_PATH,
      exact: false,
      strict: false,
    });
    const { action, botType } = (match?.params || {}) as {
      action?: string;
      botType?: string;
    };
    const isBacktesting = !!query.get(APPLIED_BACKTESTING_QUERY_PARAM);

    if (action === BotFormType.EDIT || action === BotFormType.DUPLICATE) {
      history.replace(generatePath(BASE_BOTS_PATH, { botType }));
    } else if (action === BotFormType.CREATE && isBacktesting && botType) {
      history.replace(
        getBotFormRoute(QUERY_BOT_TYPE_TO_BOT_TYPE[botType], BotFormType.CREATE)
      );
    }
  };

  const updateWorkspacePath = (workspace: WorkspaceType) => {
    if (
      currentWorkspace === WorkspaceType.ADMIN ||
      workspace === WorkspaceType.ADMIN
    ) {
      history.push(workspaceToDefaultPath[workspace]);
    } else {
      updateBotActionPath();
    }
  };

  const onWorkspaceChange = (workspace: WorkspaceType) => {
    if (isWorkspaceValid(workspace)) {
      setWorkspaceType(workspace);
      updateWorkspacePath(workspace);
    }
  };

  const getHomePath = () => {
    if (
      localStorageWorkspace === WorkspaceType.ADMIN &&
      isWorkspaceValid(localStorageWorkspace)
    )
      return workspaceToDefaultPath[WorkspaceType.ADMIN];
    else if (
      localStorageWorkspace === WorkspaceType.DEMO &&
      isWorkspaceValid(localStorageWorkspace)
    )
      return workspaceToDefaultPath[WorkspaceType.DEMO];
    return workspaceToDefaultPath[WorkspaceType.USER];
  };

  const getValidWorkspacesByPath = (pathname: string) => {
    const workspaces: WorkspaceType[] = [];
    for (let workspace of Object.values(WorkspaceType)) {
      if (isPathInWorkspace(workspace, pathname) && isWorkspaceValid(workspace))
        workspaces.push(workspace);
    }
    return workspaces;
  };

  return {
    workspace: currentWorkspace,
    isPathInWorkspace,
    onWorkspaceChange,
    getHomePath,
    getValidWorkspacesByPath,
    setWorkspaceType,
    isWorkspaceValid,
    realUser,
  };
};

export default useWorkspace;
